import { useCallback, useEffect, useMemo, useState } from "react";
import { Drawer, Modal, Select, message } from "antd";
import GooglePlacesAutocompleteInput from "../../common/GooglePlacesAutocompleteInput";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import { careService } from "../../services/careService";

const ACTIVITY_TYPES = [
  ["SOCIAL", "Social"],
  ["EXERCISE", "Exercise"],
  ["MUSIC", "Music"],
  ["CRAFTS", "Arts and crafts"],
  ["SPIRITUAL", "Spiritual"],
  ["OUTING", "Outing"],
  ["MEAL_RELATED", "Meal"],
  ["MEDICATION", "Medication"],
  ["FAMILY_VISIT", "Family Visit"],
  ["DOCTORS_APPOINTMENT", "Doctor's Appointment"],
  ["THERAPEUTIC", "Therapeutic"],
  ["OTHER", "Other"],
];

const STATUS_OPTIONS = ["ACTIVE", "COMPLETED", "CANCELLED"];

const defaultForm = () => {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  const end = new Date(now.getTime() + 60 * 60 * 1000);
  const recurringEnd = new Date(now);
  recurringEnd.setDate(recurringEnd.getDate() + 30);
  const startValue = dateTimeLocalValue(now);
  const endValue = dateTimeLocalValue(end);
  return {
    title: "",
    activityType: "SOCIAL",
    description: "",
    location: "",
    venue: "",
    address: "",
    scheduleMode: "ONE_TIME",
    startTimestamp: startValue,
    endTimestamp: endValue,
    recurrenceStartDate: startValue.slice(0, 10),
    recurrenceEndDate: dateTimeLocalValue(recurringEnd).slice(0, 10),
    dailyStartTime: startValue.slice(11, 16),
    dailyEndTime: endValue.slice(11, 16),
    recurrenceType: "ONE_TIME",
    recurrenceInterval: 1,
    recurrenceDays: [],
    recurrenceEndTimestamp: "",
    recurrenceNotes: "",
    status: "ACTIVE",
    residentParticipantMode: "SPECIFIC",
    staffParticipantMode: "SPECIFIC",
    residentIds: [],
    staffIds: [],
    notes: "",
  };
};

const asList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.content)) return value.content;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const cleanText = (value) => (typeof value === "string" ? value.trim() : "");
const firstText = (...values) => values.map(cleanText).find(Boolean) || "";
const idFor = (item = {}) =>
  firstText(
    item.careProfileId,
    item.caregiverProfileId,
    item.careGiverProfileId,
    item.profileId,
    item.residentId,
    item.patientId,
    item.userProfileId,
    item.userId,
    item.userProfile?.id,
    item.user?.id,
    item.profile?.id,
    item.profileData?.id,
    item.id,
    item.email,
    item.userProfile?.email,
    item.user?.email,
  );
const residentNameFor = (resident = {}) =>
  firstText(
    resident.name,
    resident.fullName,
    resident.displayName,
    [resident.firstName, resident.middleName, resident.lastName].filter(Boolean).join(" "),
    [resident.first_name, resident.middle_name, resident.last_name].filter(Boolean).join(" "),
    [resident.profile?.firstName, resident.profile?.middleName, resident.profile?.lastName].filter(Boolean).join(" "),
    [resident.profileData?.firstName, resident.profileData?.middleName, resident.profileData?.lastName].filter(Boolean).join(" "),
    resident.residentName,
    resident.profile?.name,
    resident.profileData?.name,
  ) || "Resident";
const staffNameFor = (staff = {}) =>
  firstText(
    staff.name,
    staff.fullName,
    staff.displayName,
    staff.caregiverName,
    staff.careGiverName,
    staff.careProfileName,
    staff.staffName,
    staff.userProfile?.name,
    staff.user?.name,
    staff.profile?.name,
    staff.profileData?.name,
    [staff.firstName, staff.middleName, staff.lastName].filter(Boolean).join(" "),
    [staff.first_name, staff.middle_name, staff.last_name].filter(Boolean).join(" "),
    [staff.userProfile?.firstName, staff.userProfile?.middleName, staff.userProfile?.lastName].filter(Boolean).join(" "),
    [staff.user?.firstName, staff.user?.middleName, staff.user?.lastName].filter(Boolean).join(" "),
    [staff.profile?.firstName, staff.profile?.middleName, staff.profile?.lastName].filter(Boolean).join(" "),
    [staff.profileData?.firstName, staff.profileData?.middleName, staff.profileData?.lastName].filter(Boolean).join(" "),
  ) || "Staff";
const emailFor = (item = {}) =>
  firstText(
    item.email,
    item.primaryEmail,
    item.workEmail,
    item.userProfile?.email,
    item.user?.email,
    item.profile?.email,
    item.profileData?.email,
    item.caregiverEmail,
    item.careGiverEmail,
  );

function dateTimeLocalValue(value = new Date()) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

const isoFromLocal = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const combineDateAndTime = (date, time) => (date && time ? `${date}T${time}` : "");

const withDatePart = (date, dateTimeValue) => {
  if (!date || !dateTimeValue) return dateTimeValue || "";
  const time = String(dateTimeValue).slice(11, 16) || "09:00";
  return `${date}T${time}`;
};

const formatTimestamp = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

const labelFor = (value) => {
  const text = String(value || "").replace(/_/g, " ").toLowerCase();
  return text ? text.replace(/\b\w/g, (letter) => letter.toUpperCase()) : "Not set";
};

const statusClassFor = (status) => String(status || "ACTIVE").toLowerCase();

const activityParticipantsText = (items, fallback = "None selected") => {
  const values = asList(items);
  if (!values.length) return fallback;
  return values
    .map((item) => [item.name || item.fullName || item.residentName || item.staffName, item.email].filter(Boolean).join(" - "))
    .filter(Boolean)
    .join(", ") || fallback;
};

const activityParticipantsCount = (items, noun) => {
  const count = asList(items).length;
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
};

const activityPlaceText = (plan = {}) => [plan.venue, plan.address, plan.location].map(cleanText).find(Boolean) || "";

const participantOptionLabel = (item = {}, fallback = "Participant") =>
  [item.name || fallback, item.email].filter(Boolean).join(" - ");

const renderParticipantOption = (item = {}, fallback = "Participant") => (
  <span className="timesheet-staff-option">
    <strong>{item.name || fallback}</strong>
    <small>{item.email || fallback}</small>
  </span>
);

const recurrenceLabel = (plan = {}) => {
  if (plan.recurrenceType === "ONE_TIME") return "One-time";
  const interval = plan.recurrenceInterval && plan.recurrenceInterval > 1 ? `Every ${plan.recurrenceInterval} ` : "";
  const days = asList(plan.recurrenceDays).length ? ` on ${plan.recurrenceDays.join(", ")}` : "";
  const until = plan.recurrenceEndTimestamp ? ` until ${formatTimestamp(plan.recurrenceEndTimestamp)}` : "";
  return `${interval}${labelFor(plan.recurrenceType)}${days}${until}`;
};

const dateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 10);
};

const timeOnly = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const monthLabel = (monthKey) => {
  const date = new Date(`${monthKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "Calendar";
  return date.toLocaleDateString([], { month: "long", year: "numeric" });
};

const addMonths = (monthKey, amount) => {
  const date = new Date(`${monthKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey(new Date()).slice(0, 7);
  date.setMonth(date.getMonth() + amount, 1);
  return dateKey(date).slice(0, 7);
};

const buildMonthDays = (monthKey) => {
  const first = new Date(`${monthKey}-01T00:00:00`);
  if (Number.isNaN(first.getTime())) return [];
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return {
      date: day,
      key: dateKey(day),
      inMonth: day.getMonth() === first.getMonth(),
      isToday: dateKey(day) === dateKey(new Date()),
    };
  });
};

const occursOnDay = (plan = {}, dayKey) => {
  if (!dayKey || !plan.startTimestamp) return false;
  const startKey = dateKey(plan.startTimestamp);
  return Boolean(startKey) && startKey === dayKey;
};

function ActivityBoard({ plans, loading, fullscreen = false, onSelectPlan, onEditPlan }) {
  return (
    <div className={`activity-planner-view-content schedule-board ${fullscreen ? "is-fullscreen" : ""}`}>
      {plans.length ? plans.map((plan) => (
        <article className={`schedule-card activity-plan-card is-${String(plan.activityType || "").toLowerCase()}`} key={plan.id} role="button" tabIndex={0} onClick={() => onSelectPlan(plan)} onKeyDown={(event) => event.key === "Enter" && onSelectPlan(plan)}>
          <div className="activity-card-head">
            <div className="schedule-card-type">
              <i className="ti ti-confetti" />
              <span>{labelFor(plan.activityType)}</span>
            </div>
            <span className={`schedule-status is-${statusClassFor(plan.status)}`}>
              <i className="ti ti-circle" />
              {labelFor(plan.status)}
            </span>
          </div>
          <div className="activity-card-main">
            <strong>{plan.title}</strong>
            <small>{recurrenceLabel(plan)}</small>
          </div>
          <div className="activity-card-meta">
            <span><i className="ti ti-clock" /> {formatTimestamp(plan.startTimestamp)} - {formatTimestamp(plan.endTimestamp)}</span>
            {activityPlaceText(plan) ? <span><i className="ti ti-map-pin" /> {activityPlaceText(plan)}</span> : null}
          </div>
          <div className="activity-participant-lines">
            <span title={activityParticipantsText(plan.residents, "No residents selected")}><i className="ti ti-users" /> {activityParticipantsCount(plan.residents, "resident")}</span>
            <span title={activityParticipantsText(plan.staff, "No staff selected")}><i className="ti ti-user-shield" /> {activityParticipantsCount(plan.staff, "staff")}</span>
          </div>
          <div className="schedule-card-foot">
            <span><i className="ti ti-eye" /> Open details</span>
            <button className="activity-card-icon-btn" type="button" title="Edit activity" onClick={(event) => { event.stopPropagation(); onEditPlan(plan); }}>
              <i className="ti ti-edit" />
            </button>
          </div>
        </article>
      )) : (
        <div className="timesheet-empty">
          <i className="ti ti-calendar-off" />
          <span>{loading ? "Loading activity plans..." : "No activity plans found for this range."}</span>
        </div>
      )}
    </div>
  );
}

function ActivityCalendar({
  calendarDays,
  calendarMonth,
  fullscreen = false,
  plansByDay,
  onCreateForDay,
  onNextMonth,
  onPreviousMonth,
  onSelectPlan,
}) {
  const visibleLimit = fullscreen ? 6 : 3;
  return (
    <div className={`activity-planner-view-content activity-planner-calendar-wrap ${fullscreen ? "is-fullscreen" : ""}`}>
      <div className="activity-planner-calendar-toolbar">
        <button className="mini-btn" type="button" onClick={onPreviousMonth}>
          <i className="ti ti-chevron-left" /> Previous
        </button>
        <strong>{monthLabel(`${calendarMonth}-01`)}</strong>
        <button className="mini-btn" type="button" onClick={onNextMonth}>
          Next <i className="ti ti-chevron-right" />
        </button>
      </div>
      <div className="activity-planner-calendar">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((weekday) => (
          <div className="activity-planner-calendar-weekday" key={weekday}>{weekday}</div>
        ))}
        {calendarDays.map((day) => {
          const dayPlans = plansByDay[day.key] || [];
          return (
            <div
              className={`activity-planner-calendar-day ${day.inMonth ? "" : "is-muted"} ${day.isToday ? "is-today" : ""}`}
              key={day.key}
              title="Double-click to add schedule activity"
              onDoubleClick={() => onCreateForDay(day.key)}
            >
              <div className="activity-planner-calendar-date">
                <span>{day.date.getDate()}</span>
                {dayPlans.length ? <small>{dayPlans.length}</small> : null}
              </div>
              <div className="activity-planner-calendar-events">
                {dayPlans.slice(0, visibleLimit).map((plan) => (
                  <button
                    className={`activity-planner-calendar-event status-${statusClassFor(plan.status)}`}
                    type="button"
                    key={`${day.key}-${plan.id}`}
                    onClick={() => onSelectPlan(plan)}
                    onDoubleClick={(event) => event.stopPropagation()}
                  >
                    <span><i className="ti ti-clock" /> {timeOnly(plan.startTimestamp)}</span>
                    <strong>{plan.title}</strong>
                  </button>
                ))}
                {dayPlans.length > visibleLimit ? <em>+{dayPlans.length - visibleLimit} more</em> : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function HomeActivityPlannerPage({ embedded = false } = {}) {
  const { user } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace } = useWorkspace();
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY ||
    "";
  const workspaceParams = useMemo(() => ({ workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId }), [selectedWorkspaceId]);
  const actorId = user?.id || user?.userId || "";
  const [plans, setPlans] = useState([]);
  const [residents, setResidents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [updateScope, setUpdateScope] = useState("OCCURRENCE");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [viewMode, setViewMode] = useState("CALENDAR");
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => dateKey(new Date()).slice(0, 7));
  const [rangeStart, setRangeStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [rangeEnd, setRangeEnd] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().slice(0, 10);
  });

  const residentChoices = useMemo(() => residents
    .map((resident) => ({ id: idFor(resident), name: residentNameFor(resident), email: emailFor(resident), source: resident }))
    .filter((resident) => resident.id), [residents]);
  const staffSource = useMemo(() => [...staff, ...asList(selectedWorkspace?.users)], [selectedWorkspace?.users, staff]);
  const staffChoices = useMemo(() => staffSource
    .map((profile) => ({ id: idFor(profile), name: staffNameFor(profile), email: emailFor(profile), source: profile }))
    .filter((profile, index, list) => profile.id && list.findIndex((item) => item.id === profile.id) === index)
    .sort((left, right) => left.name.localeCompare(right.name)), [staffSource]);
  const effectiveResidentIds = form.residentParticipantMode === "ALL" ? residentChoices.map((resident) => resident.id) : form.residentIds;
  const effectiveStaffIds = form.staffParticipantMode === "ALL" ? staffChoices.map((profile) => profile.id) : form.staffIds;

  const filteredPlans = useMemo(() => plans
    .filter((plan) => statusFilter === "ALL" || plan.status === statusFilter)
    .filter((plan) => typeFilter === "ALL" || plan.activityType === typeFilter)
    .sort((left, right) => new Date(left.startTimestamp || 0) - new Date(right.startTimestamp || 0)), [plans, statusFilter, typeFilter]);

  const activeCount = plans.filter((plan) => plan.status === "ACTIVE").length;
  const repeatingCount = plans.filter((plan) => plan.recurrenceType && plan.recurrenceType !== "ONE_TIME").length;
  const completedCount = plans.filter((plan) => plan.status === "COMPLETED").length;
  const cancelledCount = plans.filter((plan) => plan.status === "CANCELLED").length;
  const calendarDays = useMemo(() => buildMonthDays(calendarMonth), [calendarMonth]);
  const plansByCalendarDay = useMemo(() => calendarDays.reduce((grouped, day) => {
    grouped[day.key] = filteredPlans
      .filter((plan) => occursOnDay(plan, day.key))
      .sort((left, right) => new Date(left.startTimestamp || 0) - new Date(right.startTimestamp || 0));
    return grouped;
  }, {}), [calendarDays, filteredPlans]);

  const loadData = useCallback(async () => {
    if (!selectedWorkspaceId) return;
    setLoading(true);
    setError("");
    Promise.allSettled([
      careService.activityPlans.list({ ...workspaceParams, startDate: rangeStart, endDate: rangeEnd }),
      careService.residents.list({ ...workspaceParams, actorId, status: "ACTIVE", size: 200 }),
      careService.careProfiles.list({ ...workspaceParams, size: 100 }),
    ])
      .then(([planResult, residentResult, staffResult]) => {
        setPlans(planResult.status === "fulfilled" ? asList(planResult.value) : []);
        setResidents(residentResult.status === "fulfilled" ? asList(residentResult.value) : []);
        setStaff(staffResult.status === "fulfilled" ? asList(staffResult.value) : []);
        const failures = [planResult, residentResult, staffResult]
          .filter((result) => result.status === "rejected" && result.reason?.response?.status !== 404)
          .map((result) => result.reason?.response?.data?.message || result.reason?.message)
          .filter(Boolean);
        setError(failures.join(" "));
      })
      .finally(() => setLoading(false));
  }, [actorId, rangeEnd, rangeStart, selectedWorkspaceId, workspaceParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateScheduleMode = (scheduleMode) =>
    setForm((current) => ({
      ...current,
      scheduleMode,
      recurrenceType: scheduleMode === "ONE_TIME" ? "ONE_TIME" : "DAILY",
    }));

  const openNew = () => {
    setEditingPlan(null);
    setUpdateScope("OCCURRENCE");
    setForm(defaultForm());
    setEditorOpen(true);
  };

  const openNewForDay = (dayKey) => {
    const nextForm = defaultForm();
    setEditingPlan(null);
    setUpdateScope("OCCURRENCE");
    setForm({
      ...nextForm,
      startTimestamp: withDatePart(dayKey, nextForm.startTimestamp),
      endTimestamp: withDatePart(dayKey, nextForm.endTimestamp),
      recurrenceStartDate: dayKey,
      recurrenceEndDate: dayKey,
    });
    setEditorOpen(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setSelectedPlan(null);
    setUpdateScope(plan.seriesId ? "OCCURRENCE" : "OCCURRENCE");
    const planResidentIds = asList(plan.residentIds);
    const planStaffIds = asList(plan.staffIds);
    const isRecurring = plan.recurrenceType && plan.recurrenceType !== "ONE_TIME";
    const planStart = dateTimeLocalValue(plan.startTimestamp);
    const planEnd = dateTimeLocalValue(plan.endTimestamp);
    const recurrenceEnd = dateTimeLocalValue(plan.recurrenceEndTimestamp || plan.endTimestamp);
    setForm({
      title: plan.title || "",
      activityType: plan.activityType || "SOCIAL",
      description: plan.description || "",
      location: plan.location || "",
      venue: plan.venue || "",
      address: plan.address || "",
      scheduleMode: isRecurring ? "RECURRING" : "ONE_TIME",
      startTimestamp: planStart,
      endTimestamp: planEnd,
      recurrenceStartDate: planStart.slice(0, 10),
      recurrenceEndDate: recurrenceEnd.slice(0, 10),
      dailyStartTime: planStart.slice(11, 16),
      dailyEndTime: planEnd.slice(11, 16),
      recurrenceType: plan.recurrenceType || "ONE_TIME",
      recurrenceInterval: plan.recurrenceInterval || 1,
      recurrenceDays: asList(plan.recurrenceDays),
      recurrenceEndTimestamp: dateTimeLocalValue(plan.recurrenceEndTimestamp),
      recurrenceNotes: plan.recurrenceNotes || "",
      status: plan.status || "ACTIVE",
      residentParticipantMode: residentChoices.length && planResidentIds.length === residentChoices.length ? "ALL" : "SPECIFIC",
      staffParticipantMode: staffChoices.length && planStaffIds.length === staffChoices.length ? "ALL" : "SPECIFIC",
      residentIds: planResidentIds,
      staffIds: planStaffIds,
      notes: plan.notes || "",
    });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (saving || deleting) return;
    setEditorOpen(false);
    setEditingPlan(null);
  };

  const buildParticipantSnapshots = (ids, choices) =>
    choices
      .filter((choice) => ids.includes(choice.id))
      .map((choice) => ({ id: choice.id, name: choice.name, email: choice.email }));

  const savePlan = async () => {
    if (!selectedWorkspaceId) return;
    if (!form.title.trim()) {
      message.error("Activity title is required.");
      return;
    }
    if (form.scheduleMode === "ONE_TIME" && (!form.startTimestamp || !form.endTimestamp)) {
      message.error("Start and end times are required for a one-time activity.");
      return;
    }
    if (form.scheduleMode === "RECURRING" && (!form.recurrenceStartDate || !form.recurrenceEndDate || !form.dailyStartTime || !form.dailyEndTime)) {
      message.error("Recurring activities require start/end dates and daily start/end times.");
      return;
    }
    if (!effectiveResidentIds.length && !effectiveStaffIds.length) {
      message.error("Select at least one resident or staff participant.");
      return;
    }
    const startTimestamp = form.scheduleMode === "ONE_TIME" ? form.startTimestamp : combineDateAndTime(form.recurrenceStartDate, form.dailyStartTime);
    const endTimestamp = form.scheduleMode === "ONE_TIME" ? form.endTimestamp : combineDateAndTime(form.recurrenceStartDate, form.dailyEndTime);
    const recurrenceEndTimestamp = form.scheduleMode === "ONE_TIME" ? null : combineDateAndTime(form.recurrenceEndDate, form.dailyEndTime);
    const venue = form.venue.trim();
    const address = form.address.trim();
    const location = form.location.trim() || [venue, address].filter(Boolean).join(" - ");
    if (new Date(endTimestamp) <= new Date(startTimestamp)) {
      message.error(form.scheduleMode === "ONE_TIME" ? "End time must be after the start time." : "Daily end time must be after the daily start time.");
      return;
    }
    if (form.scheduleMode === "RECURRING" && new Date(form.recurrenceEndDate) < new Date(form.recurrenceStartDate)) {
      message.error("Recurring end date must be on or after the start date.");
      return;
    }
    setSaving(true);
    const payload = {
      actorId,
      workspaceId: selectedWorkspaceId,
      organizationId: selectedWorkspace?.organizationId || selectedWorkspace?.organization?.id || null,
      title: form.title.trim(),
      activityType: form.activityType,
      description: form.description.trim(),
      location,
      venue,
      address,
      startTimestamp: isoFromLocal(startTimestamp),
      endTimestamp: isoFromLocal(endTimestamp),
      recurrenceType: form.scheduleMode === "ONE_TIME" ? "ONE_TIME" : "DAILY",
      recurrenceInterval: Number(form.recurrenceInterval) || 1,
      recurrenceDays: [],
      recurrenceEndTimestamp: form.scheduleMode === "ONE_TIME" ? null : isoFromLocal(recurrenceEndTimestamp),
      recurrenceNotes: form.recurrenceNotes.trim(),
      status: form.status,
      residentIds: effectiveResidentIds,
      residents: buildParticipantSnapshots(effectiveResidentIds, residentChoices),
      staffIds: effectiveStaffIds,
      staff: buildParticipantSnapshots(effectiveStaffIds, staffChoices),
      notes: form.notes.trim(),
    };
    try {
      const saved = editingPlan?.id
        ? await careService.activityPlans.update(editingPlan.id, payload, { ...workspaceParams, updateScope })
        : await careService.activityPlans.create(payload, workspaceParams);
      if (updateScope === "FUTURE" || form.scheduleMode === "RECURRING") {
        await loadData();
      } else {
        setPlans((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      }
      setEditorOpen(false);
      setEditingPlan(null);
      message.success("Activity plan saved.");
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to save activity plan.");
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (plan = editingPlan || selectedPlan) => {
    if (!plan?.id) return;
    const confirmed = await new Promise((resolve) => {
      Modal.confirm({
        title: "Delete activity plan",
        content: "This removes the activity plan from the board.",
        okText: "Delete",
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;
    setDeleting(true);
    try {
      await careService.activityPlans.delete(plan.id, { ...workspaceParams, actorId });
      setPlans((current) => current.filter((item) => item.id !== plan.id));
      setSelectedPlan(null);
      setEditorOpen(false);
      setEditingPlan(null);
      message.success("Activity plan deleted.");
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to delete activity plan.");
    } finally {
      setDeleting(false);
    }
  };

  const content = (
    <>
      <section className="schedule-command-center">
        <div className="schedule-command-head">
          <div className="page-title">
            <span className="schedule-page-kicker"><i className="ti ti-confetti" /> Care Operations</span>
            <h1>Activities of Daily Living - ADLs</h1>
            <p>Schedule one-time and repeating activities for residents, staff, and shared home routines.</p>
          </div>
          <div className="care-staff-page-actions">
            <button className="btn primary schedule-primary-action" type="button" onClick={openNew}>
              <i className="ti ti-calendar-plus" /> Add Schedule Activity
            </button>
            <button className="mini-btn schedule-refresh-action" type="button" onClick={loadData}>
              <i className="ti ti-refresh" /> Refresh
            </button>
          </div>
        </div>
        <div className="schedule-status-strip">
          <article className="schedule-status-card is-total">
            <span><i className="ti ti-calendar-stats" /> Total</span>
            <strong>{plans.length}</strong>
            <small>{repeatingCount} repeating</small>
          </article>
          <article className="schedule-status-card is-active">
            <span><i className="ti ti-clock" /> Active</span>
            <strong>{activeCount}</strong>
            <small>Planned activities</small>
          </article>
          <article className="schedule-status-card is-completed">
            <span><i className="ti ti-circle-check" /> Completed</span>
            <strong>{completedCount}</strong>
            <small>Finished activities</small>
          </article>
          <article className="schedule-status-card is-cancelled">
            <span><i className="ti ti-ban" /> Cancelled</span>
            <strong>{cancelledCount}</strong>
            <small>Kept for history</small>
          </article>
        </div>
      </section>

      {error ? <Panel title="Activity Planner"><p className="form-error">{error}</p></Panel> : null}

      <section className="timesheet-filter-bar schedule-control-panel">
        <label>
          Type
          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
            <option value="ALL">All activity types</option>
            {ACTIVITY_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          Status
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="ALL">All statuses</option>
            {STATUS_OPTIONS.map((status) => <option value={status} key={status}>{labelFor(status)}</option>)}
          </select>
        </label>
        <div className="activity-planner-date-range">
          <span>Date range</span>
          <div>
            <label>
              From
              <input type="date" value={rangeStart} onChange={(event) => setRangeStart(event.target.value)} />
            </label>
            <label>
              To
              <input type="date" value={rangeEnd} onChange={(event) => setRangeEnd(event.target.value)} />
            </label>
          </div>
        </div>
      </section>

      <Panel
        title={viewMode === "CALENDAR" ? "Activity Calendar" : "Activity Board"}
        actionNode={(
          <div className="activity-planner-panel-actions">
            <div className="activity-planner-view-toggle" aria-label="Activity planner view">
              <button className={viewMode === "BOARD" ? "active" : ""} type="button" onClick={() => setViewMode("BOARD")}>
                <i className="ti ti-layout-board" /> Board
              </button>
              <button className={viewMode === "CALENDAR" ? "active" : ""} type="button" onClick={() => setViewMode("CALENDAR")}>
                <i className="ti ti-calendar-month" /> Calendar
              </button>
            </div>
            <button className="activity-planner-fullscreen-btn" type="button" title="Open fullscreen view" aria-label="Open fullscreen view" onClick={() => setFullscreenOpen(true)}>
              <i className="ti ti-arrows-maximize" />
            </button>
          </div>
        )}
      >
        <div className="activity-planner-content-section">
          {viewMode === "BOARD" ? (
            <ActivityBoard
              plans={filteredPlans}
              loading={loading}
              onSelectPlan={setSelectedPlan}
              onEditPlan={openEdit}
            />
          ) : (
            <ActivityCalendar
              calendarDays={calendarDays}
              calendarMonth={calendarMonth}
              plansByDay={plansByCalendarDay}
              onCreateForDay={openNewForDay}
              onPreviousMonth={() => setCalendarMonth((current) => addMonths(current, -1))}
              onNextMonth={() => setCalendarMonth((current) => addMonths(current, 1))}
              onSelectPlan={setSelectedPlan}
            />
          )}
        </div>
      </Panel>

      <Modal
        className="activity-planner-fullscreen-modal"
        width="calc(100vw - 48px)"
        open={fullscreenOpen}
        title={viewMode === "CALENDAR" ? "Activity Calendar" : "Activity Board"}
        footer={null}
        centered
        onCancel={() => setFullscreenOpen(false)}
      >
        <div className="activity-planner-fullscreen-content">
          <div className="activity-planner-fullscreen-head">
            <div className="activity-planner-view-toggle" aria-label="Fullscreen activity planner view">
              <button className={viewMode === "BOARD" ? "active" : ""} type="button" onClick={() => setViewMode("BOARD")}>
                <i className="ti ti-layout-board" /> Board
              </button>
              <button className={viewMode === "CALENDAR" ? "active" : ""} type="button" onClick={() => setViewMode("CALENDAR")}>
                <i className="ti ti-calendar-month" /> Calendar
              </button>
            </div>
          </div>
          <div className="activity-planner-content-section is-fullscreen">
            {viewMode === "BOARD" ? (
              <ActivityBoard
                plans={filteredPlans}
                loading={loading}
                fullscreen
                onSelectPlan={setSelectedPlan}
                onEditPlan={openEdit}
              />
            ) : (
              <ActivityCalendar
                calendarDays={calendarDays}
                calendarMonth={calendarMonth}
                fullscreen
                plansByDay={plansByCalendarDay}
                onCreateForDay={openNewForDay}
                onPreviousMonth={() => setCalendarMonth((current) => addMonths(current, -1))}
                onNextMonth={() => setCalendarMonth((current) => addMonths(current, 1))}
                onSelectPlan={setSelectedPlan}
              />
            )}
          </div>
        </div>
      </Modal>

      <Drawer width={920} title={editingPlan?.id ? "Edit Activity" : "Schedule Activity"} open={editorOpen} onClose={closeEditor} destroyOnClose>
        <div className="schedule-editor activity-planner-editor">
          <label>
            Activity title
            <input value={form.title} onChange={(event) => updateForm("title", event.target.value)} placeholder="Morning stretch, music hour, family outing" />
          </label>
          <div className="schedule-editor-grid">
            <label>
              Type
              <select value={form.activityType} onChange={(event) => updateForm("activityType", event.target.value)}>
                {ACTIVITY_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label>
              Status
              <select value={form.status} onChange={(event) => updateForm("status", event.target.value)}>
                {STATUS_OPTIONS.map((status) => <option value={status} key={status}>{labelFor(status)}</option>)}
              </select>
            </label>
          </div>
          {editingPlan?.seriesId ? (
            <label>
              Apply changes to
              <select value={updateScope} onChange={(event) => setUpdateScope(event.target.value)}>
                <option value="OCCURRENCE">This occurrence only</option>
                <option value="FUTURE">This and future occurrences</option>
              </select>
            </label>
          ) : null}
          <section className="activity-planner-schedule-section">
            <span className="activity-planner-section-label">Schedule</span>
            <div className="activity-planner-mode-toggle">
              <button className={form.scheduleMode === "ONE_TIME" ? "active" : ""} type="button" onClick={() => updateScheduleMode("ONE_TIME")}>
                <i className="ti ti-calendar-event" /> One-time
              </button>
              <button className={form.scheduleMode === "RECURRING" ? "active" : ""} type="button" onClick={() => updateScheduleMode("RECURRING")}>
                <i className="ti ti-repeat" /> Recurring
              </button>
            </div>
            {form.scheduleMode === "ONE_TIME" ? (
              <div className="schedule-editor-grid">
                <label>
                  Start time
                  <input type="datetime-local" value={form.startTimestamp} onChange={(event) => updateForm("startTimestamp", event.target.value)} />
                </label>
                <label>
                  End time
                  <input type="datetime-local" value={form.endTimestamp} onChange={(event) => updateForm("endTimestamp", event.target.value)} />
                </label>
              </div>
            ) : (
              <>
                <div className="schedule-editor-grid">
                  <label>
                    Start date
                    <input type="date" value={form.recurrenceStartDate} onChange={(event) => updateForm("recurrenceStartDate", event.target.value)} />
                  </label>
                  <label>
                    End date
                    <input type="date" value={form.recurrenceEndDate} onChange={(event) => updateForm("recurrenceEndDate", event.target.value)} />
                  </label>
                </div>
                <div className="schedule-editor-grid">
                  <label>
                    Daily start time
                    <input type="time" value={form.dailyStartTime} onChange={(event) => updateForm("dailyStartTime", event.target.value)} />
                  </label>
                  <label>
                    Daily end time
                    <input type="time" value={form.dailyEndTime} onChange={(event) => updateForm("dailyEndTime", event.target.value)} />
                  </label>
                </div>
                <label>
                  Recurrence notes
                  <input value={form.recurrenceNotes} onChange={(event) => updateForm("recurrenceNotes", event.target.value)} placeholder="e.g. Skip holidays or adjust for appointments" />
                </label>
              </>
            )}
          </section>
          <div className="schedule-editor-grid">
            <label>
              Venue
              <input value={form.venue} onChange={(event) => updateForm("venue", event.target.value)} placeholder="Living room, garden, community center" />
            </label>
            <label>
              Address
              {appConfigLoading && !googleMapsApiKey ? (
                <input type="text" disabled placeholder="Loading map address search..." />
              ) : (
                <GooglePlacesAutocompleteInput
                  googleMapsApiKey={googleMapsApiKey}
                  placeholder={googleMapsApiKey ? "Search address e.g. 100 Main St, Seattle" : "Enter address"}
                  types={["address"]}
                  value={form.address}
                  onChange={(value) => updateForm("address", value)}
                  onResolvePlace={({ details, prediction }) => {
                    updateForm("address", details?.formatted_address || prediction?.description || "");
                  }}
                />
              )}
            </label>
          </div>
          <section className="activity-planner-schedule-section activity-planner-participants-section">
            <span className="activity-planner-section-label">Participants</span>
            <div className="activity-planner-participant-grid">
              <div className="activity-planner-participant-panel">
                <div className="activity-planner-participant-mode">
                  <span>Residents</span>
                  <button className={form.residentParticipantMode === "ALL" ? "active" : ""} type="button" onClick={() => updateForm("residentParticipantMode", "ALL")}>All residents</button>
                  <button className={form.residentParticipantMode === "SPECIFIC" ? "active" : ""} type="button" onClick={() => updateForm("residentParticipantMode", "SPECIFIC")}>Specific residents</button>
                </div>
                {form.residentParticipantMode === "SPECIFIC" ? (
                  <label>
                    Specific residents
                    <Select
                      className="activity-planner-participant-select"
                      mode="multiple"
                      showSearch
                      allowClear
                      value={form.residentIds}
                      placeholder="Search and select residents"
                      optionFilterProp="searchText"
                      optionLabelProp="label"
                      notFoundContent="No residents found for this workspace"
                      getPopupContainer={(trigger) => trigger.parentElement || document.body}
                      onChange={(values) => updateForm("residentIds", values)}
                      options={residentChoices.map((resident) => ({
                        value: resident.id,
                        label: participantOptionLabel(resident, "Resident"),
                        searchText: participantOptionLabel(resident, "Resident"),
                        item: resident,
                      }))}
                      optionRender={(option) => renderParticipantOption(option.data.item, "Resident")}
                    />
                  </label>
                ) : (
                  <div className="activity-planner-all-participants"><i className="ti ti-users" /> {residentChoices.length} residents will be included.</div>
                )}
              </div>
              <div className="activity-planner-participant-panel">
                <div className="activity-planner-participant-mode">
                  <span>Staff</span>
                  <button className={form.staffParticipantMode === "ALL" ? "active" : ""} type="button" onClick={() => updateForm("staffParticipantMode", "ALL")}>All staff</button>
                  <button className={form.staffParticipantMode === "SPECIFIC" ? "active" : ""} type="button" onClick={() => updateForm("staffParticipantMode", "SPECIFIC")}>Specific staff</button>
                </div>
                {form.staffParticipantMode === "SPECIFIC" ? (
                  <label>
                    Specific staff
                    <Select
                      className="activity-planner-participant-select"
                      mode="multiple"
                      showSearch
                      allowClear
                      value={form.staffIds}
                      placeholder="Search and select staff"
                      optionFilterProp="searchText"
                      optionLabelProp="label"
                      notFoundContent="No staff found for this workspace"
                      getPopupContainer={(trigger) => trigger.parentElement || document.body}
                      onChange={(values) => updateForm("staffIds", values)}
                      options={staffChoices.map((profile) => ({
                        value: profile.id,
                        label: participantOptionLabel(profile, "Staff"),
                        searchText: participantOptionLabel(profile, "Staff"),
                        item: profile,
                      }))}
                      optionRender={(option) => renderParticipantOption(option.data.item, "Staff")}
                    />
                  </label>
                ) : (
                  <div className="activity-planner-all-participants"><i className="ti ti-user-shield" /> {staffChoices.length} staff will be included.</div>
                )}
              </div>
            </div>
          </section>
          <label>
            Description
            <textarea value={form.description} onChange={(event) => updateForm("description", event.target.value)} placeholder="Purpose, supplies, resident preferences, or safety notes" />
          </label>
          <label>
            Notes
            <textarea value={form.notes} onChange={(event) => updateForm("notes", event.target.value)} placeholder="Internal planning notes" />
          </label>
          <div className="drawer-actions">
            <button className="mini-btn" type="button" onClick={closeEditor}>Close</button>
            {editingPlan ? (
              <button className="mini-btn danger" type="button" onClick={() => deletePlan(editingPlan)} disabled={deleting || saving}>
                <i className="ti ti-trash" /> {deleting ? "Deleting..." : "Delete"}
              </button>
            ) : null}
            <button className="btn primary" type="button" onClick={savePlan} disabled={saving || deleting}>
              <i className="ti ti-device-floppy" /> {saving ? "Saving..." : "Save activity"}
            </button>
          </div>
        </div>
      </Drawer>

      <Drawer width={520} title="Activity Details" open={Boolean(selectedPlan)} onClose={() => setSelectedPlan(null)} destroyOnClose>
        {selectedPlan ? (
          <div className="schedule-details-drawer">
            <section>
              <span>Activity</span>
              <strong>{selectedPlan.title}</strong>
              <small>{labelFor(selectedPlan.activityType)} · {labelFor(selectedPlan.status)}</small>
            </section>
            <section className="schedule-editor-grid">
              <div><span>Starts</span><strong>{formatTimestamp(selectedPlan.startTimestamp)}</strong></div>
              <div><span>Ends</span><strong>{formatTimestamp(selectedPlan.endTimestamp)}</strong></div>
            </section>
            <section><span>Repeats</span><p>{recurrenceLabel(selectedPlan)}</p></section>
            {selectedPlan.venue ? <section><span>Venue</span><p>{selectedPlan.venue}</p></section> : null}
            {selectedPlan.address ? <section><span>Address</span><p>{selectedPlan.address}</p></section> : null}
            {!selectedPlan.venue && !selectedPlan.address && selectedPlan.location ? <section><span>Location</span><p>{selectedPlan.location}</p></section> : null}
            <section><span>Residents</span><p>{activityParticipantsText(selectedPlan.residents, "No residents selected")}</p></section>
            <section><span>Staff</span><p>{activityParticipantsText(selectedPlan.staff, "No staff selected")}</p></section>
            {selectedPlan.description ? <section><span>Description</span><p>{selectedPlan.description}</p></section> : null}
            {selectedPlan.notes ? <section><span>Notes</span><p>{selectedPlan.notes}</p></section> : null}
            <div className="drawer-actions">
              <button className="mini-btn" type="button" onClick={() => openEdit(selectedPlan)}>
                <i className="ti ti-edit" /> Edit
              </button>
              <button className="mini-btn danger" type="button" onClick={() => deletePlan(selectedPlan)} disabled={deleting}>
                <i className="ti ti-trash" /> {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        ) : null}
      </Drawer>
    </>
  );

  if (embedded) {
    return <div className="home-activity-planner-page activity-planner-embedded">{content}</div>;
  }

  return (
    <CareShell className="dashboard-content residents-page care-staff-schedules-page home-activity-planner-page">
      {content}
    </CareShell>
  );
}
